import { useState, useEffect, useCallback, useRef } from 'react';
import {
  BookOpen,
  Trophy,
  Flame,
  Target,
  Languages,
  FileQuestion,
  Medal,
  BarChart3,
  ChevronRight,
  Loader2,
  Star,
  GitBranch,
} from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import BadgeCard from '../shared/BadgeCard';

import FlashCardGame from './FlashCardGame';
import QuizSection from './QuizSection';
import BadgeShowcase from './BadgeShowcase';
import LeaderboardView from './LeaderboardView';
import ScenarioSection from './ScenarioSection';
import SectionNav from './SectionNav';


const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Sub-navigation items
const LEARNING_SECTIONS = [
  { id: 'overview', label: 'Overview', icon: BookOpen },
  { id: 'vocabulary', label: 'Vocabulary', icon: Languages },
  { id: 'quizzes', label: 'Quizzes', icon: FileQuestion },
  { id: 'scenarios', label: 'Scenarios', icon: GitBranch },
  { id: 'badges', label: 'Badges', icon: Medal },
  { id: 'leaderboard', label: 'Leaderboard', icon: BarChart3 },
];

// ============================================
// Quick Stats Card
// ============================================
const QuickStatsCard = ({ icon: Icon, label, value, subValue, color = 'purple' }) => {
  const colorClasses = {
    purple: 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
    yellow: 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400',
    green: 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400',
    orange: 'bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <p className="text-2xl font-bold">{value}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
            {subValue && (
              <p className="text-xs text-gray-400">{subValue}</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ============================================
// Learning Overview Section
// ============================================
const LearningOverview = ({ studentId, token, points, onNavigate }) => {
  const [featuredQuizzes, setFeaturedQuizzes] = useState([]);
  const [recentBadges, setRecentBadges] = useState([]);
  const [vocabProgress, setVocabProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const audioRef = useRef(null);

  const getAuthHeaders = useCallback(() => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);

  const playAudio = useCallback((audioUrl) => {
    if (audioRef.current && audioUrl) {
      audioRef.current.src = audioUrl;
      audioRef.current.play().catch(err => console.error('Audio playback failed:', err));
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch vocabulary progress for accurate mastered count
        const vocabRes = await fetch(
          `${API_BASE}/api/customer/students/${studentId}/vocabulary/progress`,
          { headers: getAuthHeaders() }
        );
        if (vocabRes.ok) {
          const vocabData = await vocabRes.json();
          setVocabProgress(vocabData);
        }

        // Fetch quizzes to find featured ones
        const quizzesRes = await fetch(
          `${API_BASE}/api/customer/students/${studentId}/quizzes`,
          { headers: getAuthHeaders() }
        );
        if (quizzesRes.ok) {
          const quizzes = await quizzesRes.json();
          setFeaturedQuizzes(quizzes.filter(q => q.is_featured && q.is_accessible).slice(0, 3));
        }

        // Fetch badges
        const badgesRes = await fetch(
          `${API_BASE}/api/customer/students/${studentId}/badges`,
          { headers: getAuthHeaders() }
        );
        if (badgesRes.ok) {
          const badges = await badgesRes.json();
          // Get recently earned badges (up to 5), sorted by earned_at descending
          const earned = badges
            .filter(b => b.earned)
            .sort((a, b) => new Date(b.earned_at) - new Date(a.earned_at))
            .slice(0, 5);
          setRecentBadges(earned);
        }
      } catch (error) {
        console.error('Failed to fetch overview data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [studentId, getAuthHeaders]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Hidden audio element for badge pronunciation */}
      <audio ref={audioRef} />

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <QuickStatsCard
          icon={Trophy}
          label="Total Points"
          value={points?.total_points || 0}
          color="yellow"
        />
        <QuickStatsCard
          icon={Flame}
          label="Vocab Streak"
          value={`${vocabProgress?.current_correct_streak || 0}`}
          subValue={`Best: ${vocabProgress?.longest_correct_streak || 0}`}
          color="orange"
        />
        <QuickStatsCard
          icon={Languages}
          label="Words Mastered"
          value={vocabProgress?.total_mastered ?? points?.vocab_terms_mastered ?? 0}
          color="purple"
        />
        <QuickStatsCard
          icon={Target}
          label="Quiz Streak"
          value={`${points?.current_quiz_streak || 0}`}
          subValue={`Best: ${points?.longest_quiz_streak || 0}`}
          color="green"
        />
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => onNavigate('vocabulary')}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-100 dark:bg-purple-900/50 rounded-xl">
                  <Languages className="h-8 w-8 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Practice Vocabulary</h3>
                  <p className="text-gray-500 dark:text-gray-400">Learn Japanese dojo terms</p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => onNavigate('quizzes')}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 dark:bg-blue-900/50 rounded-xl">
                  <FileQuestion className="h-8 w-8 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Take a Quiz</h3>
                  <p className="text-gray-500 dark:text-gray-400">Test your knowledge</p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Featured Quizzes */}
      {featuredQuizzes.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Star className="h-5 w-5 text-yellow-500" />
              Featured Quizzes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {featuredQuizzes.map(quiz => (
                <div
                  key={quiz.id}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                  onClick={() => onNavigate('quizzes')}
                >
                  <div>
                    <p className="font-medium">{quiz.title}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {quiz.question_count} questions
                      {quiz.time_limit_minutes && ` • ${quiz.time_limit_minutes} min`}
                    </p>
                  </div>
                  {quiz.passed ? (
                    <span className="text-green-600 text-sm font-medium">
                      Passed ({quiz.best_score?.toFixed(0)}%)
                    </span>
                  ) : quiz.attempt_count > 0 ? (
                    <span className="text-gray-500 text-sm">Best: {quiz.best_score?.toFixed(0)}%</span>
                  ) : (
                    <Button size="sm" variant="outline">Start</Button>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Badges */}
      {recentBadges.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Medal className="h-5 w-5 text-purple-600" />
                Recent Badges
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onNavigate('badges')}
                className="text-purple-600"
              >
                View All
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
              {recentBadges.map(badge => (
                <BadgeCard
                  key={badge.id}
                  badge={badge}
                  size="md"
                  showProgress={false}
                  onClick={() => onNavigate('badges')}
                  onPlayAudio={playAudio}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Streaks Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Flame className="h-5 w-5 text-orange-500" />
            Answer Streaks
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Vocabulary Streak - uses correct answer streak from vocab progress */}
            <div className="p-4 bg-orange-50 dark:bg-orange-900/30 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Languages className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                <h4 className="font-semibold text-orange-800 dark:text-orange-300">Vocabulary Streak</h4>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold text-orange-600 dark:text-orange-400">
                  {vocabProgress?.current_correct_streak || 0}
                </span>
                <span className="text-orange-600 dark:text-orange-400">correct in a row</span>
              </div>
              <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">
                Best: {vocabProgress?.longest_correct_streak || 0} correct answers
              </p>
              <p className="text-xs text-orange-600/70 dark:text-orange-400/70 mt-2">
                Earn bonus points at 5, 10, 25, and 50 correct answers!
              </p>
            </div>

            {/* Quiz Streak */}
            <div className="p-4 bg-green-50 dark:bg-green-900/30 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <FileQuestion className="h-5 w-5 text-green-600 dark:text-green-400" />
                <h4 className="font-semibold text-green-800 dark:text-green-300">Quiz Streak</h4>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold text-green-600 dark:text-green-400">
                  {points?.current_quiz_streak || 0}
                </span>
                <span className="text-green-600 dark:text-green-400">correct in a row</span>
              </div>
              <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                Best: {points?.longest_quiz_streak || 0} correct answers
              </p>
              <p className="text-xs text-green-600/70 dark:text-green-400/70 mt-2">
                Streak continues across all quizzes - don't miss!
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Points Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Points Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[
              { label: 'Vocabulary', value: points?.vocabulary_points || 0, color: 'bg-purple-500' },
              { label: 'Quizzes', value: points?.quiz_points || 0, color: 'bg-blue-500' },
              { label: 'Streaks', value: points?.streak_points || 0, color: 'bg-orange-500' },
              { label: 'Badges', value: points?.badge_points || 0, color: 'bg-yellow-500' },
            ].map(item => {
              const total = points?.total_points || 1;
              const percentage = Math.round((item.value / total) * 100) || 0;
              return (
                <div key={item.label}>
                  <div className="flex justify-between text-sm mb-1">
                    <span>{item.label}</span>
                    <span className="font-medium">{item.value} pts</span>
                  </div>
                  <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${item.color} rounded-full transition-all duration-300`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ============================================
// Main Learning Tab Component
// ============================================
const LearningTab = ({
  student,
  token,
  activeSection: externalActiveSection,
  onSectionChange: externalOnSectionChange,
  isSectionVisible = () => true,
  hideNav = false
}) => {
  // Use external state if provided (controlled mode), otherwise use internal state
  const [internalActiveSection, setInternalActiveSection] = useState('overview');
  const activeSection = externalActiveSection ?? internalActiveSection;
  const setActiveSection = externalOnSectionChange ?? setInternalActiveSection;

  const [points, setPoints] = useState(null);
  const [loadingPoints, setLoadingPoints] = useState(true);

  const studentId = student?.id;

  const getAuthHeaders = useCallback(() => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);

  // Fetch points data
  useEffect(() => {
    if (!studentId) return;

    const fetchPoints = async () => {
      try {
        const response = await fetch(
          `${API_BASE}/api/customer/students/${studentId}/points`,
          { headers: getAuthHeaders() }
        );
        if (response.ok) {
          const data = await response.json();
          setPoints(data);
        }
      } catch (error) {
        console.error('Failed to fetch points:', error);
      } finally {
        setLoadingPoints(false);
      }
    };

    fetchPoints();
  }, [studentId, getAuthHeaders]);

  const handleNavigate = (section) => {
    setActiveSection(section);
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'overview':
        return (
          <LearningOverview
            studentId={studentId}
            token={token}
            points={points}
            onNavigate={handleNavigate}
          />
        );
      case 'vocabulary':
        return <FlashCardGame studentId={studentId} token={token} />;
      case 'quizzes':
        return <QuizSection studentId={studentId} token={token} />;
      case 'scenarios':
        return <ScenarioSection student={student} getAuthHeaders={getAuthHeaders} />;
      case 'badges':
        return <BadgeShowcase studentId={studentId} token={token} />;
      case 'leaderboard':
        return <LeaderboardView studentId={studentId} token={token} />;
      default:
        return <LearningOverview studentId={studentId} token={token} points={points} onNavigate={handleNavigate} />;
    }
  };

  if (!studentId) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <BookOpen className="h-12 w-12 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Select a Student</h3>
          <p className="text-gray-600 dark:text-gray-400">Please select a student to view their learning progress.</p>
        </CardContent>
      </Card>
    );
  }

  // Filter sections by portal visibility
  const filteredSections = LEARNING_SECTIONS.filter(s =>
    isSectionVisible(`learning.${s.id}`)
  );

  return (
    <SectionNav
      sections={filteredSections}
      storageKey="learning_section"
      studentId={studentId}
      activeSection={activeSection}
      onSectionChange={setActiveSection}
      hideNav={hideNav}
    >
      {renderSection()}
    </SectionNav>
  );
};

export default LearningTab;
